import { useEntityNotes } from '../../components/entity-notes/useEntityNotes';
import { inNotesCell, notesGridSx } from '../../components/entity-notes/notesColumn';
import { useState, useMemo, useRef } from 'react';
import {
  Box, Button, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton,
} from '@mui/material';
import { Add, Edit, ContentCopy, Link, Visibility, VisibilityOff } from '@mui/icons-material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { DataGridPremium, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { flowsService, Flow, FlowTrackingUrls } from '../../services/flows.service';
import { adAccountsService } from '../../services/ad-accounts.service';
import { useModalState } from '../../hooks/useModalState';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import TagSelect from '../../components/TagSelect';
import EntityFilterSelect from '../../components/EntityFilterSelect';
import { EntityOption } from '../../components/entity-fetchers';
import BulkEditDialog, { BulkFieldDef } from '../../components/BulkEditDialog';
import { resolveSelectedIds, emptySelection, buildEntityWhere, applyBulkUpdate, BulkResult } from '../../utils/entity-list';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import { activityOrderBy } from '../../components/ActivityCell';
import { flowsColumns } from './flows.columns';

const BASE_BULK_FIELDS: BulkFieldDef[] = [
  { key: 'tag_ids', label: 'Tags', type: 'tags' },
  { key: 'status', label: 'Status', type: 'status' },
  { key: 'traffic_cap_limit', label: 'Traffic cap', type: 'number' },
  { key: 'daily_conversion_cap', label: 'Conversion cap (daily)', type: 'number' },
];

const EDITABLE_FIELDS = ['traffic_cap_limit'];

export default function FlowsListTab() {
  const queryClient = useQueryClient();


  const { data: adAccounts } = useQuery({
    queryKey: ['ad-accounts-for-bulk'],
    queryFn: () => adAccountsService.query({ limit: 200 }),
  });

  const bulkFields: BulkFieldDef[] = useMemo(() => ([
    ...BASE_BULK_FIELDS,
    {
      key: 'ad_account_id',
      label: 'Ad Account',
      type: 'select',
      helperText: 'Fills ad_account in the campaign URL. Choose (none) to clear it.',
      options: (adAccounts?.data || []).map((a) => ({
        value: a.id,
        label: a.fb_account_id ? `${a.name} (${a.fb_account_id})` : a.name,
      })),
    },
  ]), [adAccounts]);
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('flow');
  const [trackingDialog, setTrackingDialog] = useState<FlowTrackingUrls | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [filterTagIds, setFilterTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>());
  const [tsSel, setTsSel] = useUrlState<EntityOption[]>('ts', [], codecs.json<EntityOption[]>());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('flow', { search: debouncedSearch, statuses, tagIds: filterTagIds }, ['name'],
      tsSel.length ? [{ flow: { traffic_source_id: { op: 'in', value: tsSel.map((o) => o.id) } } }] : []),
    [debouncedSearch, statuses, filterTagIds, tsSel],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);
  const [sort, setSort] = useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
  const orderBy = activityOrderBy(sort);

  const { data, isFetching } = useQuery({
    queryKey: ['flows', where, offset, limit, sort],
    queryFn: () => flowsService.query({ where, offset, limit, ...(orderBy && { order_by: orderBy, order: sort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }) }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);

  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['flows'] });
  const notes = useEntityNotes('flow');
  const bulkAction = async (fn: () => Promise<unknown>, okMsg: string) => {
    try { await fn(); invalidate(); setSelection(emptySelection); enqueueSnackbar(okMsg, { variant: 'success' }); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : 'Action failed', { variant: 'error' }); }
  };
  const archiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => flowsService.delete(id))), `Archived ${selectedIds.length}`);
  const unarchiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => flowsService.unarchive(id))), `Unarchived ${selectedIds.length}`);
  const runBulk = async (result: BulkResult) => {
    setBulkLoading(true);
    try {
      await applyBulkUpdate((id, patch) => flowsService.update(id, patch), selectedIds, rows, result);
      invalidate(); setSelection(emptySelection); setBulkOpen(false);
      enqueueSnackbar(`Updated ${selectedIds.length} ${selectedIds.length === 1 ? 'item' : 'items'}`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Bulk update failed', { variant: 'error' });
    } finally { setBulkLoading(false); }
  };

  const processRowUpdate = async (newRow: Flow, oldRow: Flow): Promise<Flow> => {
    newRow = await notes.saveIfChanged(newRow, oldRow);
    if (newRow.traffic_cap_limit === oldRow.traffic_cap_limit) return newRow;
    const cap = Number(newRow.traffic_cap_limit);
    if (!Number.isFinite(cap) || cap < 0) throw new Error('Traffic cap must be 0 or greater');
    await flowsService.update(oldRow.id, { traffic_cap_limit: cap });
    invalidate();
    enqueueSnackbar('Updated', { variant: 'success' });
    return { ...newRow, traffic_cap_limit: cap };
  };
  const onProcessRowUpdateError = (e: unknown) => enqueueSnackbar(e instanceof Error ? e.message : 'Update failed', { variant: 'error' });

  const handleTrackingUrls = async (id: string) => {
    const urls = await flowsService.getTrackingUrls(id);
    setTrackingDialog(urls);
    setSelectedDomain(urls.available_domains?.[0] || Object.keys(urls.domain_urls)[0] || '');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied', { variant: 'success', autoHideDuration: 1500 });
  };

  const columns = flowsColumns({
    notes: notes.column,
    includeTrafficSource: true,
    adAccounts: adAccounts?.data,
    actions: {
      field: 'actions', headerName: '', width: 100, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" title="Edit" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })}>
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Duplicate" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate' })}>
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Tracking URLs" onClick={() => handleTrackingUrls(params.row.id)}>
            <Link sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  });

  return (
    <Box>
      {/* Empty on purpose: useGridHeight measures the grid's top from here. */}
      <Box ref={filterRef} />

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        sortingMode="server"
        sortModel={sort}
        onSortModelChange={setSort}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          filters: (
            <>
              <EntityFilterSelect field="traffic_source_id" value={tsSel} onChange={setTsSel} label="Traffic Source" sx={{ minWidth: 180, maxWidth: 280 }} />
              <TagSelect value={filterTagIds} onChange={setFilterTagIds} label="Filter by tags" allowCreate={false} sx={{ minWidth: 200 }} />
            </>
          ),
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'Create flow', onClick: () => setModal({ open: true, reason: 'Create' }) }],
          selectedCount: selectedIds.length,
          pluralName: 'flows',
          selectionActions: [
            { icon: Edit, hint: 'Bulk edit', onClick: () => setBulkOpen(true), color: 'inherit' as const },
            { icon: VisibilityOff, hint: 'Archive', onClick: archiveSelected, color: 'inherit' as const },
            { icon: Visibility, hint: 'Unarchive', onClick: unarchiveSelected, color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        keepNonExistentRowsSelected
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        onCellEditStart={notes.onCellEditStart}
        disableClipboardPaste
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          if (EDITABLE_FIELDS.some((f) => target.closest(`[data-field="${f}"]`))) return;
          if (notes.editable && inNotesCell(target)) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ ...notesGridSx, bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <BulkEditDialog
        open={bulkOpen}
        count={selectedIds.length}
        fields={bulkFields}
        loading={bulkLoading}
        onClose={() => setBulkOpen(false)}
        onApply={runBulk}
      />

      <Dialog open={!!trackingDialog} onClose={() => setTrackingDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Tracking URLs</DialogTitle>
        <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 2.5 } }}>
          {trackingDialog?.available_domains && trackingDialog.available_domains.length > 1 && (
            <TextField
              select
              label="Domain"
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              size="small"
            >
              {trackingDialog.available_domains.map((domain) => (
                <option key={domain} value={domain}>
                  {domain}
                </option>
              ))}
            </TextField>
          )}
          {(() => {
            const urls = trackingDialog?.domain_urls[selectedDomain];
            if (!urls) return null;
            return (
              <>
                <TextField
                  label="Campaign URL (use in ads)" fullWidth value={urls.campaign_url} sx={{ mb: 2 }}
                  slotProps={{ input: { readOnly: true, endAdornment: (
                    <IconButton onClick={() => copyToClipboard(urls.campaign_url)}><ContentCopy /></IconButton>
                  )}}}
                />
                {(() => {
                  const clickUrls = (urls.paths || [])
                    .filter((p) => p.click_through_urls?.length)
                    .flatMap((p) => p.click_through_urls!);
                  if (clickUrls.length === 0) return null;
                  return (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Click-through URLs (for landing page CTAs)
                      </Typography>
                      {clickUrls.map((o) => (
                        <TextField key={o.offer_id + o.position}
                          label={`#${o.position} ${o.offer_name}`} fullWidth value={o.url} sx={{ mb: 1 }} size="small"
                          slotProps={{ input: { readOnly: true, endAdornment: (
                            <IconButton size="small" onClick={() => copyToClipboard(o.url)}><ContentCopy fontSize="small" /></IconButton>
                          )}}}
                        />
                      ))}
                    </>
                  );
                })()}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
